import React, { forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';

interface ButtonBaseProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

interface ButtonProps extends ButtonBaseProps, React.ButtonHTMLAttributes<HTMLButtonElement> {}

interface ButtonLinkProps extends ButtonBaseProps {
  to: LinkProps['to'];
  className?: string;
  children?: React.ReactNode;
}

type ButtonComponentProps = ButtonProps | (ButtonLinkProps & { as: typeof Link });

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonComponentProps>(({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  className = '',
  ...props
}, ref) => {
  const baseStyles = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'bg-accent dark:bg-primary text-white hover:bg-accent-dark dark:hover:bg-primary-dark focus:ring-accent dark:focus:ring-primary shadow-md hover:shadow-lg transition-colors duration-300',
    secondary: 'bg-primary dark:bg-accent text-white hover:bg-primary-dark dark:hover:bg-accent-dark focus:ring-primary dark:focus:ring-accent shadow-md hover:shadow-lg transition-colors duration-300',
    outline: 'border-2 border-accent dark:border-primary text-accent dark:text-primary hover:bg-accent dark:hover:bg-primary hover:text-white focus:ring-accent dark:focus:ring-primary transition-colors duration-300',
    ghost: 'text-gray-300 dark:text-gray-600 hover:bg-dark-light dark:hover:bg-gray-100 focus:ring-gray-300 transition-colors duration-300',
    danger: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500 shadow-md hover:shadow-lg',
  };

  const sizes = {
    sm: 'px-2.5 md:px-3 py-1.5 text-xs md:text-sm gap-1.5',
    md: 'px-3 md:px-4 py-2 text-sm md:text-base gap-2',
    lg: 'px-5 md:px-6 py-2.5 md:py-3 text-base md:text-lg gap-2.5',
  };

  const isLink = 'to' in props || ('as' in props && props.as);

  if (isLink) {
    const { to, as: AsComponent = Link, ...linkProps } = props as ButtonLinkProps & { as?: typeof Link };
    return (
      <AsComponent
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={to}
        className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
        {...linkProps}
      >
        {isLoading ? (
          <svg className="animate-spin h-4 w-4 md:h-5 md:w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          leftIcon
        )}
        {children}
        {!isLoading && rightIcon}
      </AsComponent>
    );
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={(props as ButtonProps).disabled || isLoading}
      {...(props as ButtonProps)}
    >
      {isLoading ? (
        <svg className="animate-spin h-4 w-4 md:h-5 md:w-5" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        leftIcon
      )}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
